import { useMemo } from 'react';
import { useComputed } from '@preact/signals-react';
import { useFacade } from './useFacade';
import { Loader, Chart } from 'components';
import { StatisticBtn } from './components/statisticBtn/StatisticBtn';
import { StatisticBtnDynamic } from './components/statisticBtn/StatisticBtnDynamic';
import { ChartError } from './components/ChartError';
import { useParams } from 'react-router-dom';

import { Palette } from 'config';
import * as S from './CampaignReportsChart.styled';

const colors = {
  installs: {
    main: Palette.Fuchsia._500,
    background: Palette.Fuchsia._100,
  },
  impressions: {
    main: Palette.LightBlue._500,
    background: Palette.LightBlue._100,
  },
  clicks: {
    main: Palette.Violet._500,
    background: Palette.Violet._100,
  },
  cost: {
    main: Palette.Amber._500,
    background: Palette.Amber._100,
  },
  cr: {
    main: Palette.Pink._500,
    background: Palette.Pink._100,
  },
  avg_cpi: {
    main: Palette.Teal._500,
    background: Palette.Teal._100,
  },
  events: {
    main: Palette.Green._500,
    background: Palette.Green._100,
  },
  avg_cpa: {
    main: Palette.Orange._500,
    background: Palette.Orange._100,
  },
};

// list all metrics to map over later in the code (order matters)
const metrics = ['installs', 'impressions', 'clicks', 'cost', 'cr', 'avg_cpi'];

export const CampaignReportsChart = ({ dateFrom$, dateTo$ }) => {
  const { cmp_id } = useParams();
  const timePeriod$ = useComputed(() => ({ from: dateFrom$.value, to: dateTo$.value }));

  const {
    isFetchingDataChart,
    isFetchingPrevDataChart,
    isChartData,
    isTableLoaded,
    isError,
    isValidTimeRange,
    dataPerDay,
    totalValues,
    numOfDays,
    change,
    errorMsg,
    isEvents,
    currentEventInd,
    isChartHidden,
    selectedMetrics,
  } = useFacade({ timePeriod: timePeriod$.value, id: cmp_id });

  const numOfTabs = isEvents ? 8 : 6;
  const fullMetrics = useMemo(
    () => (isEvents ? [...metrics, 'events', 'avg_cpa'] : metrics),
    [isEvents, metrics],
  );

  if (
    isFetchingDataChart ||
    (!isFetchingDataChart && isChartData && !dataPerDay?.size && !isTableLoaded)
  ) {
    // height={isChartHidden ? '152px' : '579px'}
    return (
      <S.LoaderWrap height='152px'>
        <Loader.Local delay={0} />
      </S.LoaderWrap>
    );
  }

  if ((isError && !isFetchingDataChart) || !isValidTimeRange) {
    return (
      <S.ErrorContainer height='152px' isError={isValidTimeRange}>
        <ChartError message={errorMsg} isError={isValidTimeRange} />
      </S.ErrorContainer>
    );
  }

  if (!dataPerDay?.size && !isError) {
    return (
      <S.ErrorContainer height='152px' isError={false}>
        <ChartError message='No Data' isError={false} />
      </S.ErrorContainer>
    );
  }

  return (
    !!dataPerDay?.size && (
      <S.Container>
        <S.StatisticBox>
          <StatisticBtn
            label='Installs'
            value={formatNumber(totalValues.installs)}
            change={change.installs}
            numOfDays={numOfDays}
            color={colors.installs}
            metric='installs'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            numOfTabs={numOfTabs}
            isChartHidden={isChartHidden}
            selectedMetrics={selectedMetrics}
          />
          <StatisticBtn
            label='Impressions'
            change={change.impressions}
            numOfDays={numOfDays}
            value={formatNumber(totalValues.impressions)}
            color={colors.impressions}
            metric='impressions'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            numOfTabs={numOfTabs}
            isChartHidden={isChartHidden}
            selectedMetrics={selectedMetrics}
          />
          <StatisticBtn
            label='Clicks'
            change={change.clicks}
            numOfDays={numOfDays}
            value={formatNumber(totalValues.clicks)}
            color={colors.clicks}
            metric='clicks'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            numOfTabs={numOfTabs}
            isChartHidden={isChartHidden}
            selectedMetrics={selectedMetrics}
          />
          <StatisticBtn
            label='Cost'
            change={change.cost}
            numOfDays={numOfDays}
            value={`$${formatNumber(totalValues.cost)}`}
            color={colors.cost}
            metric='cost'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            numOfTabs={numOfTabs}
            isChartHidden={isChartHidden}
            selectedMetrics={selectedMetrics}
          />
          <StatisticBtn
            label='Conversion Rate'
            change={change.cr}
            numOfDays={numOfDays}
            value={`${formatNumber(totalValues.cr)}%`}
            color={colors.cr}
            metric='cr'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            numOfTabs={numOfTabs}
            isChartHidden={isChartHidden}
            selectedMetrics={selectedMetrics}
          />
          <StatisticBtn
            label='AVG CPI'
            change={change.avg_cpi}
            numOfDays={numOfDays}
            value={`$${formatNumber(totalValues.avg_cpi)}`}
            color={colors.avg_cpi}
            metric='avg_cpi'
            isFetchingPrevDataChart={isFetchingPrevDataChart}
            numOfTabs={numOfTabs}
            isChartHidden={isChartHidden}
            selectedMetrics={selectedMetrics}
          />
          {isEvents && (
            <>
              <StatisticBtnDynamic
                values={totalValues.events}
                changeList={change.events || []}
                numOfDays={numOfDays}
                color={colors.events}
                metric='events'
                isFetchingPrevDataChart={isFetchingPrevDataChart}
                numOfTabs={numOfTabs}
                isChartHidden={isChartHidden}
                selectedMetrics={selectedMetrics}
                currentEventInd={currentEventInd}
              />
              <StatisticBtn
                label='AVG CPA'
                change={change.avg_cpi}
                numOfDays={numOfDays}
                value={`$${formatNumber(Object.values(totalValues.avg_cpa)[currentEventInd])}`}
                color={colors.avg_cpa}
                metric='avg_cpa'
                isFetchingPrevDataChart={isFetchingPrevDataChart}
                numOfTabs={numOfTabs}
                isChartHidden={isChartHidden}
                selectedMetrics={selectedMetrics}
                currentEventInd={currentEventInd}
              />
            </>
          )}
        </S.StatisticBox>
        <Chart
          dataPerDay={dataPerDay}
          colors={colors}
          metrics={fullMetrics}
          selectedMetrics={selectedMetrics}
          selectedMetricsSelector='campaignReports.selectedMetricsCampaignReports'
          isChartHidden={isChartHidden}
          isEvents={isEvents}
          currentEventInd={currentEventInd}
        />
      </S.Container>
    )
  );
};

function formatNumber(number) {
  const suffixes = ['', 'K', 'M', 'B']; // thousands, millions, billions

  const suffixIndex = Math.floor(Math.log10(number) / 3);
  const scaledNumber = suffixes[suffixIndex] ? number / Math.pow(10, suffixIndex * 3) : number;
  let formattedNumber = scaledNumber?.toFixed(2);

  if (formattedNumber?.indexOf('.') !== -1) {
    formattedNumber = formattedNumber?.replace(/\.?0*$/, '');
  }

  return formattedNumber + (suffixes[suffixIndex] ? suffixes[suffixIndex] : '');
}
